// Visual metadata for the "Bamboo Bento" revamp - per-app accent colors,
// real app-icon assets, mono index labels, and monogram fallbacks.
// This is presentation-only: it adds NO marketing copy and changes NO routes.
// Keyed by the `id` field in appsData.js.
//
// `iconTone` records whether the icon artwork itself reads dark or light
// (measured mean luminance, split at ~120 of 255). The home hero weaves the
// ring by tone so dark icons do not pile into a visual mass; a new app just
// needs to declare its own. Distinct from `dark`, which picks the AppCard
// treatment.

import conduitIcon from '../assets/images/conduit-fill-icon.svg';
import ductIcon from '../assets/images/ductulator-icon.png';
import soapIcon from '../assets/images/trace-icon.png';
import starquestIcon from '../assets/images/starquest-icon.png';
import thriveIcon from '../assets/images/thrive-icon.png';
import sterlingIcon from '../assets/images/sterling-icon.png';
import chessIcon from '../assets/images/chessclock-icon.png';
import bookhoodIcon from '../assets/images/bookhood-icon.svg';
import bowlingIcon from '../assets/images/bowling-icon.png';
import dinkIcon from '../assets/images/dink-icon.png';
import cigarIcon from '../assets/images/cigar-humidor-icon.png';
import volleyballIcon from '../assets/images/volleyball-icon.png';
import mochiRunIcon from '../assets/images/mochi-run-icon.png';
import needlepointIcon from '../assets/images/needlepoint-icon.png';
import craftPricingIcon from '../assets/images/craft-pricing-icon.png';
import pcosTrackerIcon from '../assets/images/pcos-tracker-icon.png';
import tipCalculatorIcon from '../assets/images/tip-calculator-icon.png';
import snookerScoreboardIcon from '../assets/images/snooker-scoreboard-icon.png';
import dartsScorerIcon from '../assets/images/darts-scorer-icon.png';
import volleyballScoreKeeperIcon from '../assets/images/volleyball-score-keeper-icon.png';
import drumCoachIcon from '../assets/images/drum-coach-icon.png';
import quiltPlannerIcon from '../assets/images/quilt-planner-icon.png';
import lightMeterIcon from '../assets/images/lightmeter-icon.png';
import barbellCalculatorIcon from '../assets/images/barbell-calculator-icon.png';
import tennisScoreKeeperIcon from '../assets/images/tennis-score-keeper-icon.png';
import boardFootIcon from '../assets/images/boardfoot-icon.png';
import leaseTrackerIcon from '../assets/images/lease-tracker-icon.png';
import roofPitchIcon from '../assets/images/roof-pitch-icon.png';
import seatingChartIcon from '../assets/images/seating-chart-icon.png';

const HAIKU_ICON = '/haiku-icon.png'; // lives in /public

export const appMeta = {
  '1': { // Thrive
    index: 'APPx001',
    accent: '#49A56F', accentDeep: '#2F7A4E', accentSoft: '#ECF6F0',
    icon: thriveIcon, monogram: 'T', iconTone: 'dark',
  },
  '2': { // StarQuest
    index: 'APPx002',
    accent: '#4C4EAF', accentDeep: '#393B8C', accentSoft: '#ECECF8',
    icon: starquestIcon, monogram: '★', iconTone: 'dark',
  },
  '4': { // Sterling
    index: 'APPx004',
    accent: '#C75D3D', accentDeep: '#A64A30', accentSoft: '#F9EDE8',
    icon: sterlingIcon, monogram: 'S', iconTone: 'light',
  },
  '6': { // Conduit Fill
    index: 'APPx005',
    accent: '#0A84FF', accentDeep: '#0066CC', accentSoft: '#E6F1FF',
    icon: conduitIcon, iconTone: 'dark',
  },
  '8': { // Ductulator
    index: 'APPx006',
    accent: '#4A8FE0', accentDeep: '#2F6FBE', accentSoft: '#EAF2FB',
    icon: ductIcon, dark: true, iconTone: 'dark',
  },
  '7': { // Soap Calculator (route /trace)
    index: 'APPx007',
    accent: '#C8623A', accentDeep: '#A64F2E', accentSoft: '#F9EDE7',
    icon: soapIcon, iconTone: 'light',
  },
  '9': { // Haiku
    index: 'APPx008',
    accent: '#7A4B2C', accentDeep: '#5E3A22', accentSoft: '#F2EBE3',
    icon: HAIKU_ICON, iconTone: 'light',
  },
  '5': { // Bookhood (hidden)
    index: 'APPx009',
    accent: '#7B2D26', accentDeep: '#5E211C', accentSoft: '#F3E7E5',
    icon: bookhoodIcon, monogram: 'B', iconTone: 'dark',
  },
  '10': { // Chess Clock
    index: 'APPx010',
    accent: '#A66A14', accentDeep: '#7E4F0E', accentSoft: '#FBF1DD',
    icon: chessIcon, monogram: '♟', iconTone: 'light',
  },
  '11': { // Bowling Score Keeper & Tracker
    index: 'APPx011',
    accent: '#A8700F', accentDeep: '#7E5208', accentSoft: '#FBF1DD',
    icon: bowlingIcon, monogram: 'B', iconTone: 'dark',
  },
  '12': { // Dink: Pickleball Score Keeper
    index: 'APPx012',
    accent: '#1FA971', accentDeep: '#13634A', accentSoft: '#E7F3EC',
    icon: dinkIcon, monogram: 'D', iconTone: 'dark',
  },
  '13': { // Cigar Humidor & Journal (coming soon - card stays clickable to /cigar-humidor)
    index: 'APPx013',
    accent: '#9A7B36', accentDeep: '#6E5320', accentSoft: '#F4ECDA',
    icon: cigarIcon, monogram: 'C', iconTone: 'dark',
  },
  '14': { // Rotations Coach for Volleyball
    index: 'APPx014',
    accent: '#3350B4', accentDeep: '#243B86', accentSoft: '#E9ECF7',
    icon: volleyballIcon, monogram: 'V', iconTone: 'dark',
  },
  '15': { // Mochi Run (kawaii platformer game)
    index: 'APPx015',
    accent: '#EF6F9B', accentDeep: '#C24E7D', accentSoft: '#FBEAF0',
    icon: mochiRunIcon, monogram: 'M', iconTone: 'light',
  },
  '16': { // Needlepoint Studio (project tracker + stitch reference - coming soon)
    index: 'APPx016',
    accent: '#9E3B54', accentDeep: '#7C2C41', accentSoft: '#F4E6EA',
    icon: needlepointIcon, monogram: 'N', iconTone: 'light',
  },
  '17': { // Craft Pricing Calculator+ (pricing for handmade sellers - coming soon)
    index: 'APPx017',
    accent: '#C4663E', accentDeep: '#AC5330', accentSoft: '#F2E0D4',
    icon: craftPricingIcon, monogram: 'C', iconTone: 'light',
  },
  '18': { // PCOS Diet & Symptom Tracker (lab-integrated health record - coming soon)
    index: 'APPx018',
    accent: '#0F5D56', accentDeep: '#0B4741', accentSoft: '#E7EFEE',
    icon: pcosTrackerIcon, monogram: 'P', iconTone: 'light',
  },
  '19': { // Tip Calculator₊ (neo-brutalist tip calculator + bill splitter)
    index: 'APPx019',
    accent: '#9A7D0A', accentDeep: '#6F5A06', accentSoft: '#FBF4DA',
    icon: tipCalculatorIcon, monogram: '%', iconTone: 'light',
  },
  '20': { // Snooker Scoreboard₊ (dark instrument-panel frame scorer)
    index: 'APPx020',
    accent: '#C8102E', accentDeep: '#8F0B20', accentSoft: '#FBE8EB',
    icon: snookerScoreboardIcon, monogram: 'S', iconTone: 'dark',
  },
  '21': { // Darts Scorer & Dart Scoreboard (pub-OLED darts scorekeeper)
    index: 'APPx021',
    accent: '#12864B', accentDeep: '#0C5E35', accentSoft: '#E7F3EC',
    icon: dartsScorerIcon, monogram: 'D', iconTone: 'dark',
  },
  '22': { // Volleyball Score Keeper₊ (courtside OLED scoreboard)
    index: 'APPx022',
    accent: '#B07C1E', accentDeep: '#8A5F12', accentSoft: '#FAF1DE',
    icon: volleyballScoreKeeperIcon, monogram: 'V', iconTone: 'dark',
  },
  '23': { // Drum Coach: Rudiment Trainer (listening rudiment trainer)
    index: 'APPx023',
    accent: '#8A6410', accentDeep: '#6E500C', accentSoft: '#FBF3DF',
    icon: drumCoachIcon, monogram: 'D', iconTone: 'dark',
  },
  '24': { // Quilt Planner & Calculator (Log Cabin block icon, Muslin theme accent)
    index: 'APPx024',
    accent: '#9C3D34', accentDeep: '#7C3029', accentSoft: '#EFE0DA',
    icon: quiltPlannerIcon, monogram: 'Q', iconTone: 'light',
  },
  '25': { // Light Meter₊ (reticle icon on ink; the app's amber, darkened for the
          // light site - #FFB000 measures 1.7:1 on the cream card, this 4.2:1)
    index: 'APPx025',
    accent: '#9C6F00', accentDeep: '#7A5600', accentSoft: '#FBF1DA',
    icon: lightMeterIcon, monogram: '+', iconTone: 'dark',
  },
  '26': { // Barbell Calculator Plate Math (plates on a bar over near-black; the app's
          // free Steel accent darkened for the light site - #4A82B5 measures 3.8:1 on
          // the cream card, this 4.7:1. Icon mean luminance 52/255, so dark.)
    index: 'APPx026',
    accent: '#41739F', accentDeep: '#2F5675', accentSoft: '#E8EFF6',
    icon: barbellCalculatorIcon, monogram: 'B', iconTone: 'dark',
  },
  '27': { // Tennis Score Keeper₊ (umpire's chair on scoreboard ink). The app's
          // one interactive colour is optic yellow #D7F53C, which measures
          // 1.16:1 on the cream card and cannot carry text there. This is the
          // same hue taken down to 4.66:1 on cream and 4.95:1 under white.
    index: 'APPx027',
    accent: '#5C7A0F', accentDeep: '#47600A', accentSoft: '#F1F6DD',
    icon: tennisScoreKeeperIcon, monogram: 'T', iconTone: 'dark',
  },
  '28': { // Board Foot Calculator₊ (stacked boards on warm paper). The app's
          // Shop Ledger amber #C25A11 measures 4.12:1 on the cream card, which is
          // large-text only; this is the same hue at 5.22:1. Icon is paper-light.
    index: 'APPx028',
    accent: '#A84E0E', accentDeep: '#863E0A', accentSoft: '#F6E7D6',
    icon: boardFootIcon, monogram: 'B', iconTone: 'light',
  },
  '29': { // Lease Tracker: Miles Left (odometer drum icon). The app's indigo-blue
          // #3A55F5 measures 5.2:1 on the cream page and 5.5:1 on white; the deep tone is the
          // screenshots' indigo field. Icon is dark.
    index: 'APPx029',
    accent: '#3A55F5', accentDeep: '#0E1340', accentSoft: '#E6EAFE',
    icon: leaseTrackerIcon, monogram: 'L', iconTone: 'dark',
  },
  '30': { // Roof Pitch Gauge & Factor (shingled hip roof with a yellow rake line
          // on near-black). The app's shell amber #FF9900 is a mark and measures
          // 1.89:1 on cream; this is the same hue as type at 4.7:1.
    index: 'APPx030',
    accent: '#9E5C00', accentDeep: '#7C4800', accentSoft: '#FFF0DB',
    icon: roofPitchIcon, monogram: 'R', iconTone: 'dark',
  },
  '31': { // Seating Chart & Plan Maker (white round table and chairs with a gold
          // sweetheart seat, on the icon's deep forest green). The icon's gold
          // #D5AA57 is decorative only - it measures 2.04:1 on cream and is
          // never used for type.
    index: 'APPx031',
    accent: '#3A5B4D', accentDeep: '#274136', accentSoft: '#EBF1ED',
    icon: seatingChartIcon, monogram: 'S', iconTone: 'dark',
  },
};

const FALLBACK = {
  index: 'APP',
  accent: '#2F6B3F', accentDeep: '#1E4D2B', accentSoft: '#ECF3ED',
  monogram: '·',
};

export const getAppMeta = (app) => ({
  ...FALLBACK,
  monogram: app?.name ? app.name.trim().charAt(0).toUpperCase() : FALLBACK.monogram,
  ...(appMeta[app?.id] || {}),
});
